import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Share2, Trash2, MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * 카카오톡 스타일 이미지 뷰어
 * Props:
 *   images      - [{ src, senderName, createdAt, messageId, fileName, isMe }]
 *   initialIndex - 처음 보여줄 인덱스
 *   onClose     - 닫기 콜백
 *   onDownload  - (src, fileName) => void
 *   onDelete    - (messageId) => void  (본인 이미지만)
 */
export default function ImageViewer({ images, initialIndex = 0, onClose, onDownload, onDelete }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showMore, setShowMore] = useState(false);

  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const touchStartRef = useRef(null);
  const lastTapRef = useRef(0);
  const pinchStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef(null);
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const swipeDirRef = useRef(null); // 'h' | 'v' | null

  const cur = images[currentIndex];
  const total = images.length;

  // scale/offset ref 동기화
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  // 인덱스 변경 시 초기화
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    scaleRef.current = 1;
    offsetRef.current = { x: 0, y: 0 };
    setShowMore(false);
  }, [currentIndex]);

  const resetUiTimer = useCallback(() => {}, []);

  const goNext = useCallback(() => {
    if (currentIndex < total - 1) setCurrentIndex(i => i + 1);
  }, [currentIndex, total]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  }, [currentIndex]);

  // 키보드
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, goNext, goPrev]);

  // 두 손가락 사이 거리
  const pinchDist = (t) =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  // touchmove passive:false 등록
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      const touches = e.touches;

      if (touches.length === 2) {
        // 핀치 줌
        e.preventDefault();
        if (!pinchStartRef.current) return;
        const dist = pinchDist(touches);
        const newScale = Math.min(5, Math.max(1, pinchStartRef.current.scale * (dist / pinchStartRef.current.dist)));
        setScale(newScale);
        scaleRef.current = newScale;
        return;
      }

      if (touches.length === 1) {
        if (!touchStartRef.current) return;
        const dx = touches[0].clientX - touchStartRef.current.x;
        const dy = touches[0].clientY - touchStartRef.current.y;

        if (!swipeDirRef.current) {
          if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            swipeDirRef.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
          }
        }

        if (scaleRef.current > 1) {
          // 드래그 패닝
          e.preventDefault();
          isDraggingRef.current = true;
          const newOffset = {
            x: dragStartRef.current.x + dx,
            y: dragStartRef.current.y + dy,
          };
          setOffset(newOffset);
          offsetRef.current = newOffset;
        } else if (swipeDirRef.current === 'v' && dy > 0) {
          // 아래로 스와이프 (닫기)
          e.preventDefault();
        }
      }
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  const handleTouchStart = (e) => {
    const touches = e.touches;
    swipeDirRef.current = null;

    if (touches.length === 2) {
      pinchStartRef.current = {
        dist: pinchDist(touches),
        scale: scaleRef.current,
      };
      return;
    }

    pinchStartRef.current = null;
    const now = Date.now();
    const t = touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: now };
    dragStartRef.current = { x: offsetRef.current.x, y: offsetRef.current.y };

    // 더블탭 감지
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      // 더블탭 줌 토글
      if (scaleRef.current > 1) {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        scaleRef.current = 1;
        offsetRef.current = { x: 0, y: 0 };
      } else {
        setScale(2.5);
        scaleRef.current = 2.5;
      }
      return;
    }
    lastTapRef.current = now;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;

    pinchStartRef.current = null;

    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      touchStartRef.current = null;
      return;
    }

    if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && dt < 300) {
      // 단순 탭 → 메뉴 닫기
      setShowMore(false);
      touchStartRef.current = null;
      return;
    }

    if (scaleRef.current <= 1) {
      // 아래로 스와이프 → 닫기
      if (dy > 80 && swipeDirRef.current === 'v') {
        onClose();
        return;
      }
      // 좌우 스와이프 → 이전/다음
      if (Math.abs(dx) > 60 && swipeDirRef.current === 'h') {
        if (dx < 0) goNext();
        else goPrev();
      }
    }

    touchStartRef.current = null;
  };

  const handleDownload = () => {
    if (onDownload) onDownload(cur.src, cur.fileName);
    setShowMore(false);
  };

  const handleShare = async () => {
    setShowMore(false);
    try {
      if (navigator.share) {
        await navigator.share({ title: cur.fileName || '이미지', url: cur.src });
      } else {
        await navigator.clipboard.writeText(cur.src);
        alert('링크가 복사되었습니다.');
      }
    } catch {}
  };

  const handleDelete = () => {
    setShowMore(false);
    if (onDelete) onDelete(cur.messageId);
    onClose();
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const viewer = (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.96)',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none', touchAction: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={() => setShowMore(false)}
    >
      {/* 상단 바 */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
          zIndex: 2,
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <X size={24} />
        </button>

        <div style={{ textAlign: 'center', flex: 1, marginLeft: 8 }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
            {cur?.senderName || ''}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
            {formatDate(cur?.createdAt)}
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowMore(v => !v); resetUiTimer(); }}
            style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <MoreVertical size={24} />
          </button>
          {showMore && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4,
              background: '#222', borderRadius: 8, overflow: 'hidden',
              minWidth: 140, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer' }}
              >
                <Download size={16} /> 저장
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer' }}
              >
                <Share2 size={16} /> 공유
              </button>
              {cur?.isMe && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'none', border: 'none', color: '#ff4d4d', fontSize: 14, cursor: 'pointer' }}
                >
                  <Trash2 size={16} /> 삭제
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 이미지 영역 */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        {/* 이전 버튼 (PC) */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); resetUiTimer(); }}
            style={{
              position: 'absolute', left: 12, zIndex: 3,
              background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer',
            }}
          >
            <ChevronLeft size={24} />
          </button>
        )}

        <img
          ref={imgRef}
          src={cur?.src}
          alt={cur?.fileName || '이미지'}
          draggable={false}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: isDraggingRef.current ? 'none' : 'transform 0.1s ease-out',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />

        {/* 다음 버튼 (PC) */}
        {currentIndex < total - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); resetUiTimer(); }}
            style={{
              position: 'absolute', right: 12, zIndex: 3,
              background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer',
            }}
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {/* 하단 바 */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '16px 24px 24px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
          zIndex: 2,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 도트 인디케이터 */}
        {total > 1 && total <= 10 && (
          <div style={{ display: 'flex', gap: 6 }}>
            {images.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === currentIndex ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.35)',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </div>
        )}

        {/* 버튼 행 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <button
            onClick={handleDownload}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
          >
            <Download size={22} />
            <span style={{ fontSize: 11 }}>저장</span>
          </button>

          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
            {currentIndex + 1} / {total}
          </span>

          <button
            onClick={handleShare}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
          >
            <Share2 size={22} />
            <span style={{ fontSize: 11 }}>공유</span>
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(viewer, document.body);
}
